import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { autoreloadAfterSubscription, autoreloadAfterSubscriptionTimeout } from 'reactApp/appHelpers/featuresHelpers/features/noadsButton';
import { overquotaPaywallConfig } from 'reactApp/appHelpers/featuresHelpers/features/overquotaCommunications';
import { openBuyCheckout, openBuyDisableAds } from 'reactApp/modules/payment/payment.module';
import { getUserOverquotaState } from 'reactApp/modules/profile/profile.selectors';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import type { Product } from 'reactApp/types/Billing';
import { sendDwh } from 'reactApp/utils/ga';

const sendRadar = (category: string, action: string, paySource?: string, name?: string) => {
    sendDwh({
        eventCategory: category,
        dwh_enabled: true,
        action,
        dwhData: {
            name,
            paySource,
        },
    });
};

type OverquotaPaywallSource =
    | 'mnz_overquota_space_block'
    | 'mnz_block_viewer'
    | 'mnz_overquota_splash_94day'
    | 'mnz_overquota_splash_31day'
    | 'mnz_overquota_modal'
    | 'mnz_overquota_disabled_feature'
    | 'mnz_overquota_trash_informer'
    | 'mnz_overquota_banner_struct'
    | 'mnz_overquota_banner'
    | 'mnz_overquota_file_upload'
    | 'mnz_overquota_cleaner_banner';

interface Props {
    paySource: OverquotaPaywallSource;
    searchParams?: string;
}
export const useBuyClickHandlers = ({ paySource, searchParams }: Props) => {
    const storage = useSelector(getCurrentStorage);
    const dispatch = useDispatch();
    const { blockTime = undefined, clearTime, state }: ReturnType<typeof getUserOverquotaState> = useSelector(getUserOverquotaState);

    const isBought = useRef(false);
    const receiptWatched = useRef(false);
    const boughtTimer = useRef<null | NodeJS.Timeout>(null);

    useEffect(() => {
        if (boughtTimer.current) {
            clearTimeout(boughtTimer.current);
        }
    }, []);

    const onBuyClick = useCallback(
        (product?: Product) => {
            dispatch(
                openBuyCheckout({
                    title: overquotaPaywallConfig.paymentTitle,
                    onSuccess: () => {
                        sendRadar('payment', 'fastcheckout_click', paySource);

                        isBought.current = true;
                        boughtTimer.current = setTimeout(() => {
                            boughtTimer.current = null;

                            if (receiptWatched && autoreloadAfterSubscription) {
                                window.location.reload();
                            }
                        }, autoreloadAfterSubscriptionTimeout);
                    },
                    onShow: () => {
                        sendRadar('payment', 'fastcheckout_view', paySource);
                    },
                    onClose: () => {
                        if (isBought) {
                            receiptWatched.current = true;

                            if (!boughtTimer && autoreloadAfterSubscription) {
                                window.location.reload();
                            }
                        }
                    },
                    paySource,
                    productId: product?.id,
                    hideOtherTariffs: true,
                })
            );
        },
        [paySource]
    );

    const showPaywall = useCallback(() => {
        dispatch(
            openBuyDisableAds({
                name: paySource,
                title: overquotaPaywallConfig.title,
                tariffsNumber: String(overquotaPaywallConfig.tariffs),
                activeTab: overquotaPaywallConfig.activeTab,
                isFrame: false,
                actionShow: 'modal-show',
                actionSwitch: 'modal-switch',
                actionClick: 'modal-click',
                onBuyClick,
                paySource,
            })
        );
    }, [blockTime, clearTime, state, storage]);

    const goToPromoQuota = useCallback(() => {
        dispatch(
            historyPush({
                id: '/promo/quota',
                search: `?${searchParams}`,
            })
        );
    }, [blockTime, clearTime, state, storage]);

    return { showPaywall, goToPromoQuota };
};
